/**
 * @author Akash Mishra <akashmishra@eurofins.com>
 */
import { QuestionsRequestForStandardOrder, TestCodeRequestDto, TestSelectionPaginatedRequestDto } from "./../../../core/interfaces/test-code";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { ActivateKitRequest } from "src/app/core/classes/activate-kit-request";
import {
  CommercialOrderDto,
  CommercialOrderRequest,
  UpdateStandingOrderDto,
} from "src/app/core/classes/commercialOrder";
import { Diagnosis } from "src/app/core/classes/diagnosis";

import { InputPointOfCareRequest } from "src/app/core/classes/input-point-of-care-response";
import {
  CancelTgiOrderRequest,
  OrderCreation,
  OrderPatient,
  OrderPayor,
  OrderPhysician,
  OrderReferencePhysician,
  OrderSuccess,
  OrderTestParameters,
  Specimen,
  UpdateOrderRequestDto,
} from "src/app/core/classes/order-creationDto";
import { Patient } from "src/app/core/classes/patient";
import { PatientInfoDto } from "src/app/core/classes/patient-info-dto";
import { PatientPayorDetails } from "src/app/core/classes/patientPayorDetails";
import { UpdatePatientDto } from "src/app/core/classes/update-patient-dto";
import {
  FieldConfig,
  PatientAOERequest,
  QuestionRequest,
} from "src/app/core/interfaces/field-config";
import { Insurance } from "src/app/core/interfaces/insurance";
import { LinkedAccounts } from "src/app/core/interfaces/loggedInUserDto";
import { TestNameSpecimen } from "src/app/core/interfaces/test-code";
import { environment } from "src/environments/environment";
import { HttpService } from "src/app/core/services/http/http.service";

import { OrderConfirmationDetails } from "../order-confirmation/order-confirmation.component";
import { EndPoints } from "src/app/shared/EndPoints";
import { PatientInsuranceDetails } from "src/app/core/classes/patient-insurance-details";
import { InsuranceSearch } from "src/app/core/classes/insurance-search-dto";
import { DoctorsDayDetailsDto } from "src/app/core/classes/doctorsDayDetailsDto";

@Injectable({
  providedIn: "root",
})
export class OrdersService {
  private CPPortalBaseUrl: string;
  public selectedAccount: LinkedAccounts;
  public selectedPhysician: OrderPhysician;
  public noPhysicianSelected = false;
  public patientDetails: OrderPatient;
  public orderPrimaryPayorDetails: OrderPayor;
  public orderSecondaryPayorDetails: OrderPayor;
  public orderTestCodes: Array<string>;
  public insurance: Insurance;
  public insuranceList = new Array<Insurance>();
  public isReferring = false;
  public selectedReferringPhysician: OrderReferencePhysician;
  public orderSpecimen: Array<Specimen>;
  public orderCollectedOn: Date;
  public orderDawnBy: string;
  public drawChoice: string;
  public orderParameters: Array<OrderTestParameters>;
  public physicianListFetch = false;
  public confirmOrderDetails: OrderCreation;
  public orderCreateSuccess: OrderSuccess;
  public OrderParametersConfig: FieldConfig[];
  public orderDiagnosticsCodes: Array<string> = [];
  public orderTests: Array<string> = [];
  public testSpecimenName: Array<TestNameSpecimen>;
  public dynamicFormData: any;
  public resultsCount: number;
  public searchTerm: string;
  public filterType: string;
  public isOrderingPhysician: boolean;
  public selectedOrderingAccount: string;
  public doctorsDayFeatures: boolean;
  public patientCommercialOrders: any[] = [];
  private readonly buChangeSubject : Subject<void>;

  constructor(private httpService: HttpService) {
    this.CPPortalBaseUrl = environment.customerPortalServiceUrl;
    this.buChangeSubject  = new Subject();
  }

  private readonly mockjson = "assets/stub/registration.json";
  private readonly mock1json = "assets/stub/patientAOEdetails.json";
  private readonly resetFormSubject = new Subject<void>();
  resetOrderForm = this.resetFormSubject.asObservable();

  public async getPhysicianOrder(AccountCode: string) {
    const postData = {
      accountCode: AccountCode,
    };
    return this.httpService.AsyncPost(
      this.CPPortalBaseUrl + EndPoints.getPhysicianOrder,
      postData
    );
  }

  emitBUChange() {
    this.buChangeSubject.next();
  }
  
  triggerResetForm() {
    this.resetFormSubject.next();
  }

  get subcribeForBUChange(): Observable<any> {
    return this.buChangeSubject.asObservable() ;
  }

  public resetNavigationFilter() {
    this.searchTerm = undefined;
    this.filterType = undefined;
  }

  public getTestSelectionResults(AccountCode: string) {
    const testSelection = { AccountCode };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getTestSelection,
      testSelection
    );
  }

  public getTestSelectionResultsPaginated(testSelectionPaginatedRequestDto: TestSelectionPaginatedRequestDto) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getTestSelectionPaginated,
      testSelectionPaginatedRequestDto
    );
  }

    public getChildTestDetails(testDetailsByTestCodeAndVersionReqDto: string) {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getChildTestDetails}/${testDetailsByTestCodeAndVersionReqDto}`
    );
  }

  public getTestDetailsForGivenTestCodes(testCodeRequestDto: TestCodeRequestDto[]): Observable<any[]> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getTestDetailsForGivenTestCodes,
      testCodeRequestDto
    );
  }

  public getReferringPhysician(BUs: string[], searchTerm: string) {
    const val = searchTerm.replace(",", "").trim().split(" ");
    if (val[0].length < 3) {
      val.splice(0, 1);
    }
    const postData = {
      bu: BUs,
      physicianSearchCriteria: val,
    };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getReferringPhysicianOrder,
      postData
    );
  }

  public getAdditionalInformation(request: QuestionsRequestForStandardOrder) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getOrderTestUIControls,
      request
    );
  }

  public clear() {
    this.selectedAccount = undefined;
    this.selectedPhysician = undefined;
    this.physicianListFetch = false;
    this.insurance = undefined;
    this.orderPrimaryPayorDetails = undefined;
    this.orderSecondaryPayorDetails = undefined;
    this.selectedReferringPhysician = undefined;
    this.patientDetails = undefined;
  }

  public getEventsByLoggedInUserInfo(accountCodes: string[]) {
    const request = {
      accountCodes,
    };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.GetEventsByLoggedinUserInfo,
      request
    );
  }

  public getICDCodeList(searchterm: string): Observable<Diagnosis[]> {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getICDCodeList}/${searchterm}`
    );
  }

  public getInsuranceDetailsByPatientGuid(
    patientGuid: string
  ): Observable<PatientInsuranceDetails[]> {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getInsuranceDetailsByPatientId}/${patientGuid}`
    );
  }

  public getDefaultICDCodeList() {
    return this.httpService.Get(
      this.CPPortalBaseUrl + EndPoints.getDefaultICDCodeList
    );
  }

  public getLmnReasonsList(){
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.GetLmnReasonsList);
  }
  
  public getBuCodeByAccountCode(accountCode: string) {
    return this.httpService.Get(
      this.CPPortalBaseUrl + EndPoints.getBuCodeByAccountCode + accountCode
    );
  }

  public getPayorList() {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.getPayorList);
  }
  public getPayorListByInputDeatails(insuranceSearchDetails: InsuranceSearch) {
    const uri = this.CPPortalBaseUrl + EndPoints.getPayorListByInputDetails;
    return this.httpService.Post(uri, insuranceSearchDetails);
  }
  public getPatientsByPatientId(patientId: string) {
    const uri = this.CPPortalBaseUrl + EndPoints.getPatientsByPatientId;
    return this.httpService.Get(uri + "/" + patientId);
  }

  public getPatientInputDetails(
    accountCode: string[],
    patientDetails: string,
    buCode: string
  ): Observable<Patient[]> {
    const request = {
      accountCode,
      patientDetails: patientDetails.replace(",", "").trim().split(" "),
      buCode
    };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.GetPatientsByInputDetails,
      request
    );
  }

  public getDoctorsDayInfo(
    accountCode: string,
    id1SearchString: string
  ): Observable<DoctorsDayDetailsDto[]> {
    const request = {
      accountCode,
      id1SearchString,
    };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.GetDoctorsDayInfo,
      request
    );
  }

  public getCommercialOrderList(
    orderRequest: CommercialOrderRequest
  ): Observable<CommercialOrderDto[]> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getCommercialOrders,
      orderRequest
    );
  }

  public exportCommercialOrderList(
    orderRequest: CommercialOrderRequest
  ): Observable<CommercialOrderDto[]> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.exportCommercialOrders,
      orderRequest
    );
  }

  public downloadCommercialOrders(
    orderRequest: CommercialOrderRequest,
    exportFormat: number
  ) {
    return this.httpService.Post(
      this.CPPortalBaseUrl +
        EndPoints.downloadCommercialOrders +
        `/${exportFormat}`,
      orderRequest
    );
  }

  public getListOfRequisitionsForSearch(request): Observable<string[]> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getRequisitionSearchList,
      request
    );
  }

  public cancelTgiOrder(cancelTgiOrderRequest: CancelTgiOrderRequest) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.cancelTgiOrder,
      cancelTgiOrderRequest
    );
  }

  public createOrder(orderRequest: OrderCreation): Observable<any> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.CREATE_COMMERCIAL_ORDER,
      orderRequest
    );
  }

  public updateStandingOrder(updateSoRequest: UpdateStandingOrderDto): Observable<any> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.UPDATE_STANDING_ORDER,
      updateSoRequest
    );
  }
  public GetPhysicianByNPINumber(NPINumber: number): Observable<any> {
    const npis = new Array<number>();
    npis.push(NPINumber);
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getPhysiciansForGivenNPINumbers,
      npis
    );
  }
  public GetReferencePhysicianByNPINumber(NPINumber: number): Observable<any> {
    const npis = new Array<number>();
    npis.push(NPINumber);
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getReferencePhysiciansForGivenNPINumbers,
      npis
    );
  }

  public getOrderDetailsByKitId(kitId: string): Observable<any> {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getOrderDetailByKitId}${kitId}`
    );
  }
  public updateEasyOrderResult(
    request: InputPointOfCareRequest
  ): Observable<any> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.updateEasyOrderResult,
      request
    );
  }

  public getOrderConfirmationDetails(
    requisitionNumber: string
  ): Observable<OrderConfirmationDetails> {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getOrderConfirmationDetails}/${requisitionNumber}`
    );
  }

  public getQuestions(
    questionsRequest: QuestionRequest
  ): Observable<FieldConfig[]> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getQuestionsByTestCode,
      questionsRequest
    );
  }

  public getTestVersion(testCode: string): Observable<any> {
    return this.httpService.Get(`${this.CPPortalBaseUrl}
      ${EndPoints.getTestCodeVersion}${testCode}`);
  }

  public getPatientAOES(aoeRequest: PatientAOERequest): Observable<any> {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getPatientAOEs,
      aoeRequest
    );
  }

  public editPatient(request: PatientInfoDto) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.editPatient,
      request
    );
  }

  public editSpecimenKitId(request: CommercialOrderDto) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.specimenKitId,
      request
    );
  }

  public getPatientInsuranceDetails(
    patientGuid: string,
    eventCode: string
  ): Observable<PatientPayorDetails> {
    const uri = `${this.CPPortalBaseUrl}${EndPoints.getInsuranceDetails}`;
    return this.httpService.Get(`${uri}/${patientGuid}/${eventCode}`);
  }

  public getPatientsByEmail(email: string): Observable<any> {
    const emailData = { Email: email };
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getPatientByEmail,
      emailData
    );
  }
  public updatePatientByPID(request: UpdatePatientDto) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.updatePatientDetail,
      request
    );
  }
  public getEventByCodeAndPatientId(eventCode: string, patientId: string) {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getEventByCodeAndPatientId}${eventCode}/${patientId}`
    );
  }
  public activateKit(request: ActivateKitRequest) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.activateKitService,
      request
    );
  }

  public CheckIfKitIdExists(kitId: string): any {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.CHECK_IF_KITID_EXISTS}${kitId}`
    );
  }

  public GetSlotsBookedByPatientIdAndEventId(
    patientId: string,
    eventId: string
  ) {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.GetSlotsBookedByEventIdAndPatientId}${eventId}/${patientId}`
    );
  }

  public TestMapService(request: CommercialOrderDto): any {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.getkitIdFormat,
      request
    );
  }
  public getAccountDetailsForPackageCreation(accountCode: string) {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.GetAccountDetailsForPackageCreation}${accountCode}`
    );
  }

  public getSpecimensForRequisitionNumber(requisitionNumber: string) {
    return this.httpService.Get(
      `${this.CPPortalBaseUrl}${EndPoints.getSpecimensForRequisitionNumber}/${requisitionNumber}`
    );
  }

  public getPackageDetailsBylabportPackageCode(packageCode) {
    return this.httpService.Get(
      this.CPPortalBaseUrl +
        EndPoints.getPackageDetailsByLabportpackageCode +
        packageCode
    );
  }

  public isPackageInCurrentStatus(packageCode) {
    return this.httpService.Get(
      this.CPPortalBaseUrl + EndPoints.isPackageInCurrentStatus + packageCode
    );
  }

  public getBulkUploadOrderInstructions(fileName: string) {
    return this.httpService.Get(
      this.CPPortalBaseUrl + EndPoints.getBulkUploadOrderInstructions + fileName
    );
  }

  public getLmnPdfDocumentByRequisitionNumber(data): Observable<any> {
      return this.httpService.PostWithOption(
        this.CPPortalBaseUrl + EndPoints.ExportLmnAsPdf,
        data,
        { responseType: "blob" }
      );
  }

  public sendUpdateOrderRequest(updateOrderRequest: UpdateOrderRequestDto) {
    return this.httpService.Post(
      this.CPPortalBaseUrl + EndPoints.UpdateOrderRequest,
      updateOrderRequest
    );
  }
}
